import { useState, type RefObject } from "react";
import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import {
  COMP_LABELS, DED_KEYS, EARN_KEYS, EMP_KEYS, asNum, fmtAmt, fmtDate, isOpenStatus, takeHomeBeforeTax,
  type CompStatus,
} from "../../../lib/compensation.js";
import { CompRevisionDrawer, type BreakdownLine } from "../../CompRevisionDrawer.js";
import { CompVerificationPanel, type CompSnapshotDetail } from "../../CompVerificationPanel.js";
import { CompRevisionTimeline } from "../../CompRevisionTimeline.js";
import { Badge } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog.js";
import { History } from "lucide-react";
import { Field, type FieldAccess, FieldGrid, CollapsibleSection } from "../sectionShared.js";

export interface CompSnapshotFull {
  id:                     string;
  ctcAnnual:              number | string;
  bonusAnnual:            number | string;
  taxRegime:              string;
  professionalTaxMonthly: number | string;
  effectiveFrom:          string;
  effectiveTo:            string | null;
  reason:                 string;
  createdBy:              string;
  createdAt:              string;
  /** Set when Finance approved it; null on revisions that pre-date Finance verification. */
  verifiedAt?:            string | null;
  breakdown:              BreakdownLine[];
  ruleSet:                { employeeType: string; name: string } | null;
}

export interface CompHistoryItem {
  id:           string;
  ctcAnnual:    number | string;
  taxRegime:    string;
  effectiveFrom:string;
  effectiveTo:  string | null;
  reason:       string;
  createdBy:    string;
  createdAt:    string;
  status:       CompStatus;
  submittedAt:  string | null;
}

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canView: (field: string) => boolean;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  isCreate: boolean;
  /** Add / revise compensation: the API allows SALARY_ROLES only. */
  canEditComp: boolean;
  /** Finance verification: the API allows COMP_VERIFY_ROLES only. */
  isCompVerifier: boolean;
  permissions: Record<string, FieldAccess>;
  employeeId: string | null;
  ensureEmployee: () => Promise<string | null>;
  setSilentError: (v: string) => void;
  employeeType: string;
  dateOfBirth: string;
  compSnapshot: CompSnapshotFull | null; setCompSnapshot: (v: CompSnapshotFull | null) => void;
  compHistory: CompHistoryItem[]; setCompHistory: (v: CompHistoryItem[]) => void;
  showRevDrawer: boolean; setShowRevDrawer: (v: boolean) => void;
  historyExpanded: boolean; setHistoryExpanded: (updater: (prev: boolean) => boolean) => void;
  noticePeriodDays: string; setNoticePeriodDays: (v: string) => void;
  costCentre: string; setCostCentre: (v: string) => void;
  /** HRMS-0633: annual CTC parsed from an offer letter upload. When set and no
   *  snapshot exists yet, the "Add Compensation" button advertises that fact
   *  and the drawer opens with CTC + Reason=NEW_HIRE pre-filled. */
  offerLetterCtc?: number | null;
}

export function CompensationSection({
  sectionRef, canView, inputProps, isCreate, canEditComp, isCompVerifier, permissions,
  employeeId, ensureEmployee, setSilentError, employeeType, dateOfBirth,
  compSnapshot, setCompSnapshot, compHistory, setCompHistory,
  showRevDrawer, setShowRevDrawer, historyExpanded, setHistoryExpanded,
  noticePeriodDays, setNoticePeriodDays, costCentre, setCostCentre,
  offerLetterCtc,
}: Props) {
  const [editRevision, setEditRevision] = useState<CompSnapshotDetail | null>(null);
  const [showCurrentBreakdown, setShowCurrentBreakdown] = useState(false);
  const [compView, setCompView] = useState<"monthly" | "yearly">("yearly"); // opens on the yearly view

  // History is newest first. At most one revision is open (pending or rejected) at a time.
  const openRevision   = compHistory.find(h => isOpenStatus(h.status)) ?? null;
  const latestApproved = compHistory.find(h => h.status === "APPROVED") ?? null;
  const canAddRevision = canEditComp && !openRevision;
  const masked = !isCreate && permissions["compensationRecords"]?.canViewInProfile === "MASKED";
  // A resubmit keeps the same revision id, so the panel and the comparison remount on
  // this instead — otherwise they keep showing the figures from before the edit.
  const historyVersion = compHistory.map(h => `${h.id}:${h.status}:${h.submittedAt ?? ""}`).join("|");

  function refreshCompensation() {
    api.get<CompSnapshotFull | null>(`/employees/${employeeId}/compensation/current`)
      .then(r => setCompSnapshot(r.data)).catch((err) => logError("EmployeeProfilePage.CompRevision.refreshSnapshot", err));
    api.get<CompHistoryItem[]>(`/employees/${employeeId}/compensation/snapshots`)
      .then(r => setCompHistory(r.data)).catch((err) => logError("EmployeeProfilePage.CompRevision.refreshHistory", err));
  }

  async function addOrReviseCompensation() {
    const eid = employeeId ?? await ensureEmployee();
    if (!eid) { setSilentError("Fill in First Name and Email above before adding compensation."); return; }
    setEditRevision(null); setShowRevDrawer(true);
  }

  return (
    <CollapsibleSection id="compensation" sectionRef={sectionRef} title="Compensation & Terms">

      {/* Unconfirmed revision waiting for (or sent back by) Finance */}
      {!masked && openRevision && employeeId && (
        <CompVerificationPanel
          key={historyVersion}
          employeeId={employeeId}
          snapshotId={openRevision.id}
          baselineId={latestApproved?.id ?? null}
          isVerifier={isCompVerifier}
          canEditComp={canEditComp}
          onChanged={refreshCompensation}
          onEdit={d => { setEditRevision(d); setShowRevDrawer(true); }}
        />
      )}

      {/* Snapshot / CTC display */}
      {masked ? (
        <div style={{ padding: "12px 0", color: "var(--color-surface-500)", fontSize: 13, fontStyle: "italic" }}>
          Compensation details are masked for your role.
        </div>
      ) : !compSnapshot ? (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8, padding:"12px 0", color:"var(--color-surface-500)", fontSize:13 }}>
          <span>{openRevision ? "No verified compensation yet." : "No compensation record yet."}</span>
          {canAddRevision && (
            // HRMS-0633: if the offer letter already told us the annual CTC, say so on the button
            // so HR knows this is a one-click confirm, not a blank form to fill in.
            offerLetterCtc && offerLetterCtc > 0 ? (
              <button onClick={() => void addOrReviseCompensation()} style={{ padding:"6px 14px", background:"var(--color-brand-600)", color:"#fff", border:"none", borderRadius:"var(--radius-md)", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                Create compensation from offer letter (₹{fmtAmt(offerLetterCtc)})
              </button>
            ) : (
              <button onClick={() => void addOrReviseCompensation()} style={{ padding:"6px 14px", background:"var(--color-brand-600)", color:"#fff", border:"none", borderRadius:"var(--radius-md)", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                Add Compensation
              </button>
            )
          )}
        </div>
      ) : (() => {
        const ctc    = asNum(compSnapshot.ctcAnnual);
        const bdList = compSnapshot.breakdown ?? [];
        // Bonus is a yearly payout — excluded from the monthly Gross Pay figure below.
        const grossPay = EARN_KEYS.filter(k => k !== "BONUS").reduce((s,k) => s + asNum(bdList.find(l=>l.componentKey===k)?.annualAmount??0), 0);
        // Workbook "Take Home before tax" (legacy): Standard subtracts gratuity + total PF/ESI + insurance.
        const netPay   = takeHomeBeforeTax(bdList);
        // Monthly / Yearly toggle: tiles show annual or annual ÷ 12; bonus only appears yearly.
        const yearly  = compView === "yearly";
        const per     = (annual: number) => (yearly ? annual : annual / 12);
        const sfx     = yearly ? "/yr" : "/mo";

        return (
          <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:12 }}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-bold tabular-nums">₹{fmtAmt(ctc)} CTC</span>
                  {compSnapshot.verifiedAt
                    ? <Badge tone="success">✓ Finance verified · {fmtDate(compSnapshot.verifiedAt)}</Badge>
                    : <Badge tone="neutral">Before Finance verification</Badge>}
                </div>
                <div style={{ fontSize:11, color:"var(--color-surface-500)", marginTop:2 }}>
                  Effective {new Date(compSnapshot.effectiveFrom).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                  {" · "}{compSnapshot.reason.replace(/_/g," ")}
                  {" · "}{compSnapshot.taxRegime.replace(/_/g," ")}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {compHistory.length > 0 && employeeId && (
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setHistoryExpanded(() => true)}>
                    <History className="h-3.5 w-3.5" /> Revision history ({compHistory.length})
                  </Button>
                )}
                {canAddRevision && (
                  <button onClick={() => void addOrReviseCompensation()} style={{ padding:"6px 14px", background:"var(--color-brand-600)", color:"#fff", border:"none", borderRadius:"var(--radius-md)", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    New Revision
                  </button>
                )}
              </div>
            </div>

            {bdList.length > 0 && (
              <div style={{ border:"1px solid var(--color-surface-200)", borderRadius:"var(--radius-md)", overflow:"hidden", marginBottom:12 }}>
                {/* On load: just the three headline numbers. The line-by-line split is one click away. */}
                <FieldGrid cols="three" gap="sm" style={{ padding:"10px 14px", background:"var(--color-surface-50)" }}>
                  {[
                    { label:"Total CTC /yr", val:ctc,         color:"var(--color-surface-900)" },
                    { label:`Gross Pay ${sfx}`, val:per(grossPay), color:"var(--color-success-accent)" },
                    { label:`Take Home ${sfx}`, val:per(netPay),   color:"var(--color-info-accent)" },
                  ].map(({ label: l, val, color }) => (
                    <div key={l} style={{ textAlign:"center" }}>
                      <div style={{ fontSize:10, color:"var(--color-surface-400)", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:14, fontWeight:700, color, fontVariantNumeric:"tabular-nums" }}>₹{fmtAmt(val)}</div>
                    </div>
                  ))}
                </FieldGrid>
                <div className="flex items-center justify-between gap-2" style={{ borderTop:"1px solid var(--color-surface-100)", padding:"4px 8px" }}>
                  <Button type="button" variant="ghost" size="sm" className="gap-1 px-1 text-brand-600" aria-expanded={showCurrentBreakdown} onClick={() => setShowCurrentBreakdown(v => !v)}>
                    {showCurrentBreakdown ? "▲ Hide" : "▼ Show"} salary breakdown
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button type="button" size="sm" variant={!yearly ? "default" : "ghost"} className="h-6 px-2 text-[11px]" onClick={() => setCompView("monthly")}>Monthly</Button>
                    <Button type="button" size="sm" variant={yearly ? "default" : "ghost"} className="h-6 px-2 text-[11px]" onClick={() => setCompView("yearly")}>Yearly</Button>
                  </div>
                </div>
                {showCurrentBreakdown && (
                  <FieldGrid cols="three" gap="none" style={{ borderTop:"1px solid var(--color-surface-100)", background:"var(--color-surface-0)" }}>
                    {[["Earnings","var(--color-success-accent)", EARN_KEYS],["Deductions","var(--color-warning-fg-strong)", DED_KEYS],["Employer Cost","var(--color-surface-400)", EMP_KEYS]].map(([title, color, keys]) => (
                      <div key={String(title)} style={{ padding:"8px 12px", borderRight:"1px solid var(--color-surface-100)" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:String(color), textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>{String(title)}</div>
                        {(keys as string[]).map(k => { const l = bdList.find(x=>x.componentKey===k); if(!l||asNum(l.annualAmount)===0) return null;
                          if (k === "BONUS" && !yearly) return null; // bonus is paid yearly, not part of monthly salary
                          return (
                          <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"2px 0", color:"var(--color-surface-700)" }}>
                            <span>{COMP_LABELS[k]??k}</span>
                            <span style={{ fontVariantNumeric:"tabular-nums" }}>{yearly ? fmtAmt(asNum(l.annualAmount)) : fmtAmt(asNum(l.monthlyAmount))}{sfx}</span>
                          </div>
                        ); })}
                      </div>
                    ))}
                  </FieldGrid>
                )}
              </div>
            )}

            {/* Revision history: every version is kept; nothing is overwritten. Opens in a modal. */}
            {employeeId && (
              <Dialog open={historyExpanded} onOpenChange={o => setHistoryExpanded(() => o)}>
                <DialogContent className="max-w-5xl">
                  <DialogHeader>
                    <DialogTitle>Revision history</DialogTitle>
                    <DialogDescription>
                      Every compensation revision is kept — nothing is overwritten. Pick two revisions to compare them.
                    </DialogDescription>
                  </DialogHeader>
                  {historyExpanded && <CompRevisionTimeline key={historyVersion} employeeId={employeeId} history={compHistory} />}
                </DialogContent>
              </Dialog>
            )}
          </>
        );
      })()}

      {/* Notice Period + Cost Centre (employee record fields, always editable) */}
      <FieldGrid cols="auto" gap="md" style={{ marginTop: compSnapshot || isCreate ? 14 : 0 }}>
        {(isCreate || canView("noticePeriodDays")) && (
          <Field label="Notice Period (days)">
            <input type="number" {...inputProps("noticePeriodDays", noticePeriodDays, setNoticePeriodDays)} />
          </Field>
        )}
        {(isCreate || canView("costCentre")) && (
          <Field label="Cost Centre">
            <input {...inputProps("costCentre", costCentre, setCostCentre)} />
          </Field>
        )}
      </FieldGrid>

      {/* Revision drawer */}
      <CompRevisionDrawer
        open={showRevDrawer}
        employeeId={employeeId ?? ""}
        employeeType={employeeType || undefined}
        dateOfBirth={dateOfBirth || undefined}
        revision={editRevision}
        // HRMS-0633: pre-fill from the parsed offer letter when there's no snapshot yet.
        // (The drawer itself ignores this when `revision` is set, i.e. on resubmits.)
        initialCtcFromOfferLetter={!compSnapshot && !editRevision ? (offerLetterCtc ?? null) : null}
        onClose={() => {
          setShowRevDrawer(false);
          setEditRevision(null);
          setTimeout(refreshCompensation, 200);
        }}
        onCreated={() => {
          setShowRevDrawer(false);
          setEditRevision(null);
          refreshCompensation();
        }}
      />

    </CollapsibleSection>
  );
}
